import * as React from 'react';
import styles from './index.less';
import { connect } from 'dva';
import { Dispatch } from 'redux';
import { ConnectState, CollectionCreatorPageModelState } from '@/models/connect';
import FComponentsLib from '@freelog/components-lib';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import * as AHooks from 'ahooks';
import { OnMount_Page_Action, OnUnmount_Page_Action } from '@/models/collectionCreatorPage';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FIcons from '@/components/FIcons';
import {
  FPodcastRssImportingBanner,
  FPodcastRssSyncErrorBanner,
  FPodcastRssSyncErrorModal,
  resolveRssSyncBannerState,
  buildRssSyncErrorMessages,
  RssSyncProgressData,
} from '@/components/FPodcastRssSubmit';

interface ResourceCreatorProps {
  dispatch: Dispatch;
  collectionCreatorPage: CollectionCreatorPageModelState;
}

function ResourceCreator({
                           dispatch,
                           collectionCreatorPage,
                         }: ResourceCreatorProps) {


  AHooks.useMount(() => {
    dispatch<OnMount_Page_Action>({
      type: 'collectionCreatorPage/onMount_Page',
    });
  });

  AHooks.useUnmount(() => {
    dispatch<OnUnmount_Page_Action>({
      type: 'collectionCreatorPage/onUnmount_Page',
    });
  });

  const [$syncData, set$syncData, get$syncData] =
    FUtil.Hook.useGetState<RssSyncProgressData | null>(null);
  const [$errorModalOpen, set$errorModalOpen] = React.useState<boolean>(false);

  const resourceID = collectionCreatorPage.step1_createdResourceInfo?.resourceID;
  const rssImportActive =
    collectionCreatorPage.step2_rssImportProcessing &&
    collectionCreatorPage.step >= 3 &&
    !!resourceID;
  const bannerState = resolveRssSyncBannerState($syncData);

  React.useEffect(() => {
    if (!rssImportActive || !resourceID) {
      set$syncData(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const fetchProgress = async () => {
      const { ret, errCode, data } = await FServiceAPI.Rss.getSyncProgress({
        resourceId: resourceID,
      });
      if (cancelled || ret !== 0 || errCode !== 0 || !data) return;
      set$syncData(data);
    };
    (async () => {
      await fetchProgress();
      if (cancelled) return;
      timer = setInterval(fetchProgress, 60 * 1000);
    })();
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [rssImportActive, resourceID]);

  return (<div className={styles.page}>
    {rssImportActive && bannerState.kind === 'importing' && (
      <FPodcastRssImportingBanner />
    )}
    {rssImportActive && bannerState.kind === 'multiple' && (
      <FPodcastRssSyncErrorBanner mode='multiple' onView={() => set$errorModalOpen(true)} />
    )}
    {rssImportActive && bannerState.kind === 'sync_failed' && (
      <FPodcastRssSyncErrorBanner mode='sync_failed' />
    )}
    {rssImportActive && bannerState.kind === 'single_item' && (
      <FPodcastRssSyncErrorBanner
        mode='single_item'
        itemTitle={bannerState.itemTitle}
        errorMessage={bannerState.errorMessage}
      />
    )}
    <div className={styles.styles}>

    <div className={styles.left}>
      <div className={styles.pageTitle}>
        {/* 发行合集 */}
        <FComponentsLib.FTitleText text={FI18n.i18nNext.t('cqr_title')} type={'h1'} />
      </div>

      <div className={styles.steps}>
        <div
          className={[styles.step, collectionCreatorPage.step === 1 ? styles.stepActivated : '', collectionCreatorPage.step > 1 ? styles.stepFinished : ''].join(' ')}>
          <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <FIcons.FArrowRight className={styles.iconFArrowRight} />
            <FIcons.FCheckMark className={styles.iconFCheckMark} />
          </div>
          {/* 创建合集 */}
          <span className={styles.title}>{FI18n.i18nNext.tAuto('cqr_step1')}</span>
        </div>

        <div
          className={[styles.step, collectionCreatorPage.step === 2 ? styles.stepActivated : '', collectionCreatorPage.step > 2 ? styles.stepFinished : ''].join(' ')}>
          <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <FIcons.FArrowRight className={styles.iconFArrowRight} />
            <FIcons.FCheckMark className={styles.iconFCheckMark} />
          </div>
          {/* 添加单品 */}
          <span className={styles.title}>{FI18n.i18nNext.tAuto('cqr_step2')}</span>
        </div>

        <div>
          <div
            className={[styles.step,
              collectionCreatorPage.step === 3
                ? styles.stepActivated
                : '',
              collectionCreatorPage.step > 3 && collectionCreatorPage.step3_policies.length > 0
                ? styles.stepFinished
                : ''].join(' ')}>
            <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              <FIcons.FArrowRight className={styles.iconFArrowRight} />
              <FIcons.FCheckMark className={styles.iconFCheckMark} />
            </div>
            {/* 添加授权策略 */}
            <span className={styles.title}>{FI18n.i18nNext.tAuto('cqr_step3')}</span>
          </div>

          {
            collectionCreatorPage.step === 3 && (<div style={{
              padding: '5px 0 0 31px',
              width: 190,
              fontSize: 12,
              color: '#000',
              lineHeight: '18px',
              opacity: .4,
            }}>
              {FI18n.i18nNext.tAuto('cqr_authplan_info')}
            </div>)
          }
        </div>

        <div>
          <div className={[styles.step, collectionCreatorPage.step === 4 ? styles.stepActivated : ''].join(' ')}>
            <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              <FIcons.FArrowRight className={styles.iconFArrowRight} />
              <FIcons.FCheckMark className={styles.iconFCheckMark} />
            </div>
            {/* 完善合集信息 */}
            <span className={styles.title}>{FI18n.i18nNext.tAuto('cqr_step4')}</span>
          </div>
          {
            collectionCreatorPage.step === 4 && (<div style={{
              padding: '5px 0 0 31px',
              width: 190,
              fontSize: 12,
              color: '#000',
              lineHeight: '18px',
              opacity: .4,
            }}>
              {FI18n.i18nNext.tAuto('cqr_listing_info')}
            </div>)
          }
        </div>


      </div>
    </div>
    <div className={styles.right}>
      {collectionCreatorPage.step === 1 && (<Step1 />)}
      {collectionCreatorPage.step === 2 && (<Step2 />)}
      {collectionCreatorPage.step === 3 && (<Step3 />)}
      {collectionCreatorPage.step === 4 && (<Step4 />)}
    </div>
  </div>

    <FPodcastRssSyncErrorModal
      open={$errorModalOpen}
      onClose={() => set$errorModalOpen(false)}
      messages={buildRssSyncErrorMessages(get$syncData())}
    />
  </div>);
}

export default connect(({ collectionCreatorPage }: ConnectState) => ({
  collectionCreatorPage: collectionCreatorPage,
}))(ResourceCreator);
